import { Loader2, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { createTemplate, createTemplateWithImage } from '@/api/services/nutrition'
import { PhotoInput } from '@/components/shared/photo-input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}

export function TemplateCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const [foodText, setFoodText] = useState('')
  const [name, setName] = useState('')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const { isSubmitting: busy, run } = useSubmitLock()

  useEffect(() => {
    if (!open) return
    setFoodText('')
    setName('')
    setImageFiles([])
    setImagePreviews([])
  }, [open])

  const hasImage = imageFiles.length > 0
  const canSubmit = foodText.trim().length > 0 || hasImage

  const handleCreate = async () => {
    if (!canSubmit) return
    await run(async () => {
      try {
        if (hasImage) {
          await createTemplateWithImage(imageFiles[0], foodText.trim(), name.trim() || null)
        } else {
          await createTemplate({ text: foodText.trim(), name: name.trim() || null })
        }
        toast.success('お気に入りを登録しました')
        onCreated()
        onOpenChange(false)
      } catch {
        toast.error('登録に失敗しました')
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>お気に入りを追加</DialogTitle>
          <DialogDescription className="text-xs">食事内容を入力・画像を追加してAI解析で登録します</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">テンプレート名（省略可）</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="テンプレート名" />
          </div>

          <div className="space-y-1">
            <PhotoInput
              label="食事内容"
              files={imageFiles}
              previews={imagePreviews}
              onFilesChange={(files, previews) => {
                setImageFiles(files)
                setImagePreviews(previews)
              }}
              maxFiles={1}
            />
            <Textarea
              value={foodText}
              onChange={(e) => setFoodText(e.target.value)}
              rows={4}
              placeholder="例: 松屋の牛めし並盛、味噌汁"
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleCreate} disabled={busy || !canSubmit} className="w-full gap-1">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
            AI解析して登録
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
